/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    XMarkIcon,
    ServerStackIcon,
    ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { volumeService, currencyService } from '../../services/api';
import toast from 'react-hot-toast';
import { BillingPeriod, NFSCostEstimate } from '../../types';
import { validateVolumeName } from '../../utils/volumeName';

interface CreateVolumeModalProps {
    isOpen: boolean;
    onClose: () => void;
    projectId: string;
    onVolumeCreated?: (volume: any) => void;
}

const billingPeriods: { value: BillingPeriod; label: string }[] = [
    { value: 'month', label: 'Monthly' },
    { value: 'quarter', label: 'Quarterly' },
    { value: 'annual', label: 'Annual' },
];

const CreateVolumeModal: React.FC<CreateVolumeModalProps> = ({
    isOpen,
    onClose,
    projectId,
    onVolumeCreated,
}) => {
    const queryClient = useQueryClient();
    const [formData, setFormData] = useState({
        name: '',
        quota_gb: 10,
        billing_period: 'month' as BillingPeriod,
    });
    const [error, setError] = useState<string | null>(null);

    const { data: currencyData } = useQuery({
        queryKey: ['currency-rates'],
        queryFn: () => currencyService.get().then((res) => res.data),
        staleTime: 5 * 60_000,
    });
    const gstRate: number = currencyData?.gst_rate ?? 18;

    const { data: costEstimate, isFetching: isEstimating } = useQuery<NFSCostEstimate>({
        queryKey: ['volume-estimate', projectId, formData.quota_gb, formData.billing_period],
        queryFn: () =>
            volumeService
                .estimateCost(projectId, {
                    size_gb: formData.quota_gb,
                    billing_period: formData.billing_period,
                })
                .then((res) => res.data as NFSCostEstimate),
        enabled: isOpen && !!projectId && formData.quota_gb > 0,
        staleTime: 30_000,
    });

    const createMutation = useMutation({
        mutationFn: (data: typeof formData) =>
            volumeService.create(projectId, {
                name: data.name.trim(),
                quota_gb: data.quota_gb,
                nfs_enabled: true,
                billing_period: data.billing_period,
            }),
        onSuccess: (response) => {
            toast.success('Volume created successfully');
            queryClient.invalidateQueries({ queryKey: ['volumes', projectId] });
            if (onVolumeCreated) {
                onVolumeCreated(response.data);
            }
            handleClose();
        },
        onError: (err: any) => {
            setError(err.response?.data?.error || 'Failed to create volume');
        },
    });

    const handleClose = () => {
        setFormData({ name: '', quota_gb: 10, billing_period: 'month' });
        setError(null);
        onClose();
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        const nameError = validateVolumeName(formData.name);
        if (nameError) {
            setError(nameError);
            return;
        }

        if (formData.quota_gb < 1) {
            setError('Quota must be at least 1 GB');
            return;
        }

        createMutation.mutate(formData);
    };

    if (!isOpen) return null;

    const modalContent = (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-900 border border-gray-800 rounded-2xl shadow-xl max-w-md w-full">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-800">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-green-500/20 rounded-lg">
                            <ServerStackIcon className="w-6 h-6 text-green-500" />
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-white">Create NFS Volume</h3>
                            <p className="text-sm text-gray-400">Persistent storage for your pods</p>
                        </div>
                    </div>
                    <button
                        onClick={handleClose}
                        className="p-2 text-gray-400 hover:text-gray-300 hover:bg-gray-800 rounded-lg"
                    >
                        <XMarkIcon className="w-5 h-5" />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    {error && (
                        <div className="p-3 bg-red-500/20 border border-red-500/30 rounded-lg flex items-start gap-2">
                            <ExclamationTriangleIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                            <p className="text-sm text-red-400">{error}</p>
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">
                            Volume Name
                        </label>
                        <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => {
                                const name = e.target.value;
                                setFormData({ ...formData, name });
                                setError(name.trim() ? validateVolumeName(name) : null);
                            }}
                            className="input"
                            placeholder="my-data-volume"
                        />
                        <p className="text-xs text-gray-400 mt-1">Letters, numbers, and hyphens only - no dots or spaces</p>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">
                            Storage Size (GB)
                        </label>
                        <div className="flex items-center gap-3">
                            <input
                                type="range"
                                min="1"
                                max="1000"
                                step="1"
                                value={formData.quota_gb}
                                onChange={(e) => setFormData({ ...formData, quota_gb: parseInt(e.target.value) })}
                                className="flex-1 h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-green-500"
                            />
                            <input
                                type="number"
                                min="1"
                                max="1000"
                                value={formData.quota_gb}
                                onChange={(e) => setFormData({ ...formData, quota_gb: Math.max(1, parseInt(e.target.value) || 1) })}
                                className="w-20 px-3 py-2 text-center border border-gray-600 bg-gray-800 text-white rounded-lg focus:ring-2 focus:ring-green-500"
                            />
                            <span className="text-sm text-gray-400">GB</span>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">
                            Billing Period
                        </label>
                        <select
                            value={formData.billing_period}
                            onChange={(e) => setFormData({ ...formData, billing_period: e.target.value as BillingPeriod })}
                            className="input"
                        >
                            {billingPeriods.map((period) => (
                                <option key={period.value} value={period.value}>
                                    {period.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Live cost estimate from the backend (/storage/billing/estimate). */}
                    {formData.quota_gb > 0 && (
                        <div className="bg-primary-500/10 border border-primary-500/30 rounded-lg p-3">
                            <p className="text-xs font-medium text-primary-400">Estimated Cost</p>
                            {costEstimate ? (
                                <>
                                    <p className="text-xs text-primary-400 mt-0.5">
                                        {formData.quota_gb} GB × ₹{costEstimate.rate_per_gb_per_hour.toFixed(4)}/GB/hr
                                        {costEstimate.discount_percent > 0 &&
                                            ` (−${costEstimate.discount_percent.toFixed(0)}% ${formData.billing_period} discount)`}
                                    </p>
                                    <div className="rounded-md border border-gray-700 bg-gray-900/40 p-3 space-y-1 text-xs mt-2">
                                        <div className="flex justify-between text-gray-300">
                                            <span>Subtotal</span>
                                            <span className="font-mono">₹{costEstimate.period_cost.toFixed(2)}</span>
                                        </div>
                                        <div className="flex justify-between text-gray-400">
                                            <span>GST ({gstRate}%)</span>
                                            <span className="font-mono">+ ₹{(costEstimate.period_cost * gstRate / 100).toFixed(2)}</span>
                                        </div>
                                        <div className="flex justify-between border-t border-gray-700 pt-1 text-sm">
                                            <span className="text-gray-100 font-medium">Total</span>
                                            <span className="font-mono text-primary-300 font-semibold">₹{(costEstimate.period_cost * (1 + gstRate / 100)).toFixed(2)}/{formData.billing_period}</span>
                                        </div>
                                    </div>
                                    <p className="text-xs text-gray-400 mt-1">
                                        Debited upfront; full {formData.billing_period} is billed - no refund on early deletion.
                                    </p>
                                </>
                            ) : (
                                <p className="text-xs text-gray-400 mt-0.5">
                                    {isEstimating ? 'Calculating…' : 'Unable to fetch estimate.'}
                                </p>
                            )}
                        </div>
                    )}

                    <div className="pt-4 flex gap-3">
                        <button
                            type="button"
                            onClick={handleClose}
                            className="flex-1 btn-secondary"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={createMutation.isPending}
                            className="flex-1 btn-primary disabled:opacity-50"
                        >
                            {createMutation.isPending ? 'Creating...' : 'Create Volume'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );

    return createPortal(modalContent, document.body);
};

export default CreateVolumeModal;
